<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import type { PropType } from 'vue'
import ActionButton from '../components/ui-components/ActionButton.vue'
import Viewer2 from './Viewer2.vue'
import { getStrings } from './pickerLanguage'
import { XMarkIcon } from '@heroicons/vue/24/outline'
import { collection, doc, updateDoc, Timestamp } from 'firebase/firestore'
import { useFirestore } from 'vuefire'
import { BundleModelFactory } from 'monocle-models'
import { z } from 'zod'

const emit = defineEmits(['close', 'confirm'])
const languageStrings = computed(() => {
  return getStrings(props.language)
})

const { BundleDBModel } = new BundleModelFactory(Timestamp)

const props = defineProps({
  bundle: {
    type: Object as PropType<z.infer<typeof BundleDBModel>>,
    required: true,
  },
  currentBundleId: {
    type: String,
    required: true,
  },
  language: {
    type: String,
    required: true,
  },
  shortcode: {
    type: String,
    required: true,
  },
})

const shareBundle = (currentBundleId: string) => {
  //set the bundle to shared
  const bundleCollection = collection(useFirestore(), 'bundles')
  const bundleDoc = doc(bundleCollection, currentBundleId)
  updateDoc(bundleDoc, {
    'owner_editable.shared': true,
    'owner_editable.details_public.title': true,
    'owner_editable.details_public.description': true,
  })
}

const closePopup = () => {
  emit('close')
}

// Create a reactive key for the Viewer2 component that updates when model data changes
const viewerKey = computed(() => {
  const bundleId = props.currentBundleId
  const editModified = (
    props.bundle.owner_editable.modified || props.bundle.metadata.created
  ).getTime()

  // Use edit_modified timestamp to force viewer refresh when model files change
  return editModified ? `${bundleId}-${editModified}` : bundleId
})

const popupRef = ref(null)

onClickOutside(popupRef, closePopup)
</script>

<template>
  <div>
    <!-- <Transition name="fade"> -->
    <div
      class="main-container"
      ref="popupRef">
      <XMarkIcon
        class="close-button"
        @click="closePopup" />
      <Viewer2
        v-if="shortcode"
        :key="viewerKey"
        :bundle-id="currentBundleId"
        :shortcode="shortcode"
        :hide-logo="true"
        class="model-viewer"
        enable-rotation
        enable-interaction-prompt
        interaction-prompt-style="basic" />
      <div class="button-wrapper">
        <ActionButton
          :label="languageStrings.cancel"
          @click="
            () => {
              closePopup()
            }
          " />
        <ActionButton
          :label="languageStrings.confirm"
          @click="
            () => {
              closePopup()
              shareBundle(currentBundleId)
              emit('confirm', currentBundleId)
            }
          " />
      </div>
    </div>
    <!-- </Transition> -->
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.main-container {
  background-color: var(--background-color);
  width: 80vw;
  height: 70vh;
  max-width: 1200px;
  max-height: 800px;
  position: fixed;
  border-radius: 25px;
  border: 2px solid var(--background-reverse);
  left: 50%;
  top: 50%;
  transform: translate(-50%, -70%);
  display: flex;
  flex-wrap: wrap;
  z-index: 10;
  justify-content: center;
  color: var(--text-color);
  margin-top: 10vh;
}

.model-viewer {
  height: 80%;
  width: 100%;
  margin-top: 2rem;
}

.button-wrapper {
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  margin-bottom: 2rem;
}

.close-button {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  cursor: pointer;
  color: var(--text-color);
  cursor: pointer;
  z-index: 100;
}
</style>
